import { motion } from "framer-motion";
import { 
  GraduationCap, 
  Download, 
  Calendar,
  Play,
  FileText,
  Clock,
  Users,
  Star,
} from "lucide-react";
import { Container, SectionHeading, SectionSubheading } from "./shared";

function CourseProductCard() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
      className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-lg group hover:shadow-xl transition-shadow"
    >
      {/* Thumbnail */}
      <div className="relative aspect-video bg-gradient-to-br from-emerald-500 to-teal-600">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur flex items-center justify-center group-hover:scale-110 transition-transform">
            <Play className="w-6 h-6 text-white ml-0.5" />
          </div>
        </div>
        <div className="absolute top-3 left-3 bg-black/60 backdrop-blur rounded-full px-2.5 py-1 text-xs text-white font-medium">
          Course
        </div>
        <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur rounded-full px-2.5 py-1 text-xs text-white flex items-center gap-1">
          <Clock className="w-3 h-3" />
          8h 30m
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4">
        <h4 className="font-semibold text-foreground mb-1">Complete Web Development</h4>
        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">Master modern web development from HTML to React and beyond.</p>
        
        <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
          <span className="flex items-center gap-1">
            <Users className="w-3.5 h-3.5" />
            1,234 students
          </span>
          <span className="flex items-center gap-1">
            <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
            4.9
          </span>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-lg font-bold text-foreground">$99</span>
          <button className="px-4 py-2 bg-emerald-500 text-white text-sm font-medium rounded-lg hover:bg-emerald-600 transition-colors">
            Enroll Now
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function DownloadProductCard() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
      viewport={{ once: true }}
      className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-lg group hover:shadow-xl transition-shadow"
    >
      {/* Thumbnail */}
      <div className="relative aspect-video bg-gradient-to-br from-slate-600 to-slate-800 flex items-center justify-center">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-20 h-20 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
            <FileText className="w-10 h-10 text-white" />
          </div>
        </div>
        <div className="absolute top-3 left-3 bg-black/60 backdrop-blur rounded-full px-2.5 py-1 text-xs text-white font-medium">
          Download
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4">
        <h4 className="font-semibold text-foreground mb-1">Design System Templates</h4>
        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">Premium Figma templates with components and styles.</p>
        
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3 flex-wrap">
          <span className="flex items-center gap-1 bg-neutral-100 dark:bg-neutral-800 px-2 py-1 rounded">
            <FileText className="w-3 h-3" />
            PDF
          </span>
          <span className="flex items-center gap-1 bg-neutral-100 dark:bg-neutral-800 px-2 py-1 rounded">
            Figma
          </span>
          <span className="flex items-center gap-1 bg-neutral-100 dark:bg-neutral-800 px-2 py-1 rounded">
            12 files
          </span>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-lg font-bold text-foreground">$29</span>
          <button className="px-4 py-2 bg-slate-700 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors flex items-center gap-2">
            <Download className="w-4 h-4" />
            Get Instant Access
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function BookingProductCard() {
  const slots = ["10:00 AM", "2:00 PM", "4:00 PM"];
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      viewport={{ once: true }}
      className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-lg group hover:shadow-xl transition-shadow"
    >
      {/* Thumbnail */}
      <div className="relative aspect-video bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-20 h-20 bg-white/20 backdrop-blur rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
            <Calendar className="w-10 h-10 text-white" />
          </div>
        </div>
        <div className="absolute top-3 left-3 bg-black/60 backdrop-blur rounded-full px-2.5 py-1 text-xs text-white font-medium">
          1:1 Session
        </div>
        <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur rounded-full px-2.5 py-1 text-xs text-white flex items-center gap-1">
          <Clock className="w-3 h-3" />
          60 min
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4">
        <h4 className="font-semibold text-foreground mb-1">Career Coaching Session</h4>
        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">Personal guidance to accelerate your career growth.</p>
        
        <div className="mb-3">
          <div className="text-xs text-muted-foreground mb-2">Available slots:</div>
          <div className="flex gap-2 flex-wrap">
            {slots.map((slot, i) => (
              <button
                key={i}
                className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                  i === 0 
                    ? 'bg-orange-500/10 border-orange-500/30 text-orange-500' 
                    : 'border-neutral-200 dark:border-neutral-700 text-muted-foreground hover:border-orange-500/30'
                }`}
              >
                {slot}
              </button>
            ))}
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-lg font-bold text-foreground">$149</span>
          <button className="px-4 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 transition-colors">
            Book Session
          </button>
        </div>
      </div>
    </motion.div>
  );
}

const productTypes = [
  {
    icon: GraduationCap,
    title: "Video Courses",
    description: "Multi-lesson courses with chapters, progress tracking, and community access",
  },
  {
    icon: Download,
    title: "Digital Downloads",
    description: "PDFs, templates, code packages — instant delivery after purchase",
  },
  {
    icon: Calendar,
    title: "1:1 Bookings",
    description: "Coaching sessions, consultations, and mentorship calls",
  },
];

export function ProductTypes() {
  return (
    <section className="py-16 md:py-24 lg:py-32 bg-background">
      <Container>
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <SectionHeading className="mb-4">
              Multiple Ways to{" "}
              <span className="text-emerald-500">Monetize</span>
            </SectionHeading>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <SectionSubheading className="mx-auto">
              Whether you teach, create resources, or offer coaching — Lint supports all your digital products.
            </SectionSubheading>
          </motion.div>
        </div>

        {/* Product Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <CourseProductCard />
          <DownloadProductCard />
          <BookingProductCard />
        </div>

        {/* Feature row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-8 border-t border-neutral-200 dark:border-neutral-800">
          {productTypes.map((type, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1, duration: 0.4 }}
              viewport={{ once: true }}
              className="flex items-start gap-3"
            >
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                <type.icon className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground mb-1">{type.title}</h4>
                <p className="text-sm text-muted-foreground">{type.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </Container>
    </section>
  );
}

